import React, { useState, useRef, useEffect } from 'react';
import { RefreshCw, TreePine, ChevronDown, ChevronUp, BarChart3, Home, Settings, User, Shield, Crown, LogOut, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

function UserListHeader({ onRefresh, refreshing, theme, currentThemeName }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleProfileClick = () => {
    navigate('/profile');
    setShowDropdown(false);
  };

  const handleAnalyticsClick = () => {
    navigate('/analytics');
    setShowDropdown(false);
  };

  const handleHomeClick = () => {
    navigate('/fullchat');
    setShowDropdown(false);
  };

  const handleSettingsClick = () => {
    navigate('/settings');
    setShowDropdown(false);
  };

  const handleChatClick = () => {
    navigate('/fullchat');
    setShowDropdown(false);
  };

  return (
    <div className="animate-fade-in-up" style={{ 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'space-between',
      marginBottom: 32,
      padding: '24px 0'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginLeft: 20 }}>
        {/* Oakland AI Logo */}
        <div
          onClick={() => navigate('/fullchat')}
          role="link"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate('/fullchat');
            }
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            cursor: 'pointer'
          }}
          title="Go to main page"
        >
          <div style={{
            fontSize: '28px',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #3b82f6 0%, #22c55e 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '0.02em'
          }}>
            Oakland AI
          </div>
        </div>
        <div style={{ 
          width: 1, 
          height: 40, 
          background: currentThemeName === 'dark' ? 'rgba(255, 255, 255, 0.1)' : theme.border,
          margin: '0 16px'
        }} />
        <div>
          <h1 style={{ 
            fontSize: 32, 
            fontWeight: 700, 
            color: currentThemeName === 'dark' 
              ? 'white' 
              : currentThemeName === 'beige'
              ? '#5d4037'
              : '#1f2937',
            margin: '0 0 8px 0'
          }}>
            User Management
          </h1>
          <p style={{ 
            fontSize: 16, 
            color: currentThemeName === 'dark' 
              ? '#e5e7eb' 
              : currentThemeName === 'beige'
              ? '#8d6e63'
              : '#6b7280',
            margin: 0
          }}>
            Manage user accounts, status, and permissions
          </p>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        {/* Refresh Button */}
        <button
          onClick={onRefresh}
          disabled={refreshing}
          style={{
            padding: '12px',
            borderRadius: 12,
            border: currentThemeName === 'dark'
              ? '1px solid rgba(59, 130, 246, 0.2)'
              : currentThemeName === 'beige'
              ? '1px solid rgba(139, 69, 19, 0.15)'
              : '1px solid rgba(59, 130, 246, 0.1)',
            background: currentThemeName === 'dark'
              ? 'rgba(30, 41, 59, 0.8)'
              : currentThemeName === 'beige'
              ? 'rgba(245, 245, 220, 0.9)'
              : 'rgba(255, 255, 255, 0.95)',
            color: currentThemeName === 'dark' ? '#93c5fd' : theme.text,
            cursor: refreshing ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(10px)',
            transition: 'all 0.2s ease',
            opacity: refreshing ? 0.6 : 1
          }}
          onMouseEnter={(e) => !refreshing && (e.target.style.transform = 'scale(1.05)')}
          onMouseLeave={(e) => !refreshing && (e.target.style.transform = 'scale(1)')}
        >
          <RefreshCw 
            size={18} 
            className={refreshing ? 'animate-spin' : ''}
            style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }}
          />
        </button>

        {/* Navigation Dropdown */}
        <div className="navigation-dropdown-container" ref={dropdownRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              background: showDropdown 
                ? currentThemeName === 'dark'
                  ? 'rgba(59, 130, 246, 0.2)'
                  : currentThemeName === 'beige'
                  ? 'rgba(139, 69, 19, 0.15)'
                  : 'rgba(59, 130, 246, 0.1)'
                : currentThemeName === 'dark'
                ? 'rgba(30, 41, 59, 0.8)'
                : currentThemeName === 'beige'
                ? 'rgba(245, 245, 220, 0.9)'
                : 'rgba(255, 255, 255, 0.95)',
              border: `1px solid ${showDropdown 
                ? currentThemeName === 'dark'
                  ? 'rgba(59, 130, 246, 0.3)'
                  : currentThemeName === 'beige'
                  ? 'rgba(139, 69, 19, 0.2)'
                  : 'rgba(59, 130, 246, 0.2)'
                : currentThemeName === 'dark'
                ? 'rgba(59, 130, 246, 0.2)'
                : currentThemeName === 'beige'
                ? 'rgba(139, 69, 19, 0.15)'
                : 'rgba(59, 130, 246, 0.1)'
              }`,
              borderRadius: 12,
              color: currentThemeName === 'dark' ? '#93c5fd' : theme.text,
              cursor: 'pointer',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.2s ease',
              fontWeight: 600,
              fontSize: 14
            }}
            onMouseEnter={(e) => {
              if (!showDropdown) {
                e.target.style.background = currentThemeName === 'dark'
                  ? 'rgba(59, 130, 246, 0.15)'
                  : currentThemeName === 'beige'
                  ? 'rgba(139, 69, 19, 0.12)'
                  : 'rgba(59, 130, 246, 0.08)';
              }
            }}
            onMouseLeave={(e) => {
              if (!showDropdown) {
                e.target.style.background = currentThemeName === 'dark'
                  ? 'rgba(30, 41, 59, 0.8)'
                  : currentThemeName === 'beige'
                  ? 'rgba(245, 245, 220, 0.9)'
                  : 'rgba(255, 255, 255, 0.95)';
              }
            }}
          >
            <BarChart3 size={16} />
            <span>Navigation</span>
            {showDropdown ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>

          {/* Dropdown Menu */}
          {showDropdown && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '8px',
              background: currentThemeName === 'dark'
                ? 'rgba(15, 23, 42, 0.95)'
                : currentThemeName === 'beige'
                ? 'rgba(245, 245, 220, 0.95)'
                : 'rgba(255, 255, 255, 0.95)',
              border: `1px solid ${currentThemeName === 'dark'
                ? 'rgba(59, 130, 246, 0.2)'
                : currentThemeName === 'beige'
                ? 'rgba(139, 69, 19, 0.15)'
                : 'rgba(59, 130, 246, 0.1)'
              }`,
              borderRadius: 12,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              backdropFilter: 'blur(20px)',
              minWidth: '220px',
              zIndex: 9999,
              animation: 'slideInDown 0.2s ease',
              overflow: 'hidden'
            }}>
              {/* User Info Section */}
              <div style={{
                padding: '16px',
                borderBottom: `1px solid ${currentThemeName === 'dark'
                  ? 'rgba(59, 130, 246, 0.2)'
                  : currentThemeName === 'beige'
                  ? 'rgba(139, 69, 19, 0.15)'
                  : 'rgba(59, 130, 246, 0.1)'
                }`,
                background: currentThemeName === 'dark'
                  ? 'rgba(30, 41, 59, 0.5)'
                  : currentThemeName === 'beige'
                  ? 'rgba(245, 245, 220, 0.5)'
                  : 'rgba(255, 255, 255, 0.5)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #22c55e 100%)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: '600',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
                  }}>
                    {user?.name?.charAt(0)?.toUpperCase() || user?.firstName?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: currentThemeName === 'dark' ? 'white' : theme.text,
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
                    }}>
                      {`${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.firstName || user?.displayName || 'User'}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: currentThemeName === 'dark' ? '#9ca3af' : theme.textTertiary,
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
                    }}>
                      {user?.email || 'user@example.com'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Menu Items */}
              <div style={{ padding: '8px' }}>
                {/* Home Page */}
                <button
                  onClick={handleHomeClick}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    width: '100%',
                    padding: '12px 16px',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: '8px',
                    color: currentThemeName === 'dark' ? 'white' : theme.text,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                    fontSize: '14px',
                    textAlign: 'left'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = currentThemeName === 'dark'
                      ? 'rgba(59, 130, 246, 0.1)'
                      : currentThemeName === 'beige'
                      ? 'rgba(139, 69, 19, 0.08)'
                      : 'rgba(59, 130, 246, 0.05)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <Home size={16} />
                  <span>Chat Page</span>
                </button>

                <button
                  onClick={() => {
                    navigate('/admin');
                    setShowDropdown(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    width: '100%',
                    padding: '12px 16px',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: '8px',
                    color: currentThemeName === 'dark' ? 'white' : theme.text,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                    fontSize: '14px',
                    textAlign: 'left'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = currentThemeName === 'dark'
                      ? 'rgba(59, 130, 246, 0.1)'
                      : currentThemeName === 'beige'
                      ? 'rgba(139, 69, 19, 0.08)'
                      : 'rgba(59, 130, 246, 0.05)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <Shield size={16} />
                  <span>Admin</span>
                  <div style={{
                    marginLeft: 'auto',
                    fontSize: '10px',
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontWeight: '600'
                  }}>
                    ADMIN
                  </div>
                </button>

                {/* Analytics Page */}
                <button
                  onClick={handleAnalyticsClick}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    width: '100%',
                    padding: '12px 16px',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: '8px',
                    color: currentThemeName === 'dark' ? 'white' : theme.text,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                    fontSize: '14px',
                    textAlign: 'left'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = currentThemeName === 'dark'
                      ? 'rgba(59, 130, 246, 0.1)'
                      : currentThemeName === 'beige'
                      ? 'rgba(139, 69, 19, 0.08)'
                      : 'rgba(59, 130, 246, 0.05)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <BarChart3 size={16} />
                  <span>Analytics Dashboard</span>
                  <div style={{
                    marginLeft: 'auto',
                    fontSize: '10px',
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontWeight: '600'
                  }}>
                    PRO
                  </div>
                </button>

                {/* Chat Page */}
                <button
                  onClick={handleChatClick}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    width: '100%',
                    padding: '12px 16px',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: '8px',
                    color: currentThemeName === 'dark' ? 'white' : theme.text,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                    fontSize: '14px',
                    textAlign: 'left'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = currentThemeName === 'dark'
                      ? 'rgba(59, 130, 246, 0.1)'
                      : currentThemeName === 'beige'
                      ? 'rgba(139, 69, 19, 0.08)'
                      : 'rgba(59, 130, 246, 0.05)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <MessageCircle size={16} />
                  <span>Chat Page</span>
                </button>

                {/* Profile Page */}
                <button
                  onClick={handleProfileClick}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    width: '100%',
                    padding: '12px 16px',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: '8px',
                    color: currentThemeName === 'dark' ? 'white' : theme.text,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                    fontSize: '14px',
                    textAlign: 'left'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = currentThemeName === 'dark'
                      ? 'rgba(59, 130, 246, 0.1)'
                      : currentThemeName === 'beige'
                      ? 'rgba(139, 69, 19, 0.08)'
                      : 'rgba(59, 130, 246, 0.05)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <User size={16} />
                  <span>Profile Page</span>
                </button>

                {/* Settings Page */}
                <button
                  onClick={handleSettingsClick}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    width: '100%',
                    padding: '12px 16px',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: '8px',
                    color: currentThemeName === 'dark' ? 'white' : theme.text,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                    fontSize: '14px',
                    textAlign: 'left'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = currentThemeName === 'dark'
                      ? 'rgba(59, 130, 246, 0.1)'
                      : currentThemeName === 'beige'
                      ? 'rgba(139, 69, 19, 0.08)'
                      : 'rgba(59, 130, 246, 0.05)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <Settings size={16} />
                  <span>Settings</span>
                </button>



                {/* Divider */}
                <div style={{
                  height: '1px',
                  background: currentThemeName === 'dark'
                    ? 'rgba(59, 130, 246, 0.2)'
                    : currentThemeName === 'beige'
                    ? 'rgba(139, 69, 19, 0.15)'
                    : 'rgba(59, 130, 246, 0.1)',
                  margin: '8px 16px'
                }} />

                {/* Log Out */}
                <button
                  onClick={handleLogout}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    width: '100%',
                    padding: '12px 16px',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#ef4444',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                    fontSize: '14px',
                    textAlign: 'left'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <LogOut size={16} />
                  <span>Log Out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UserListHeader; 